import { useEffect, useMemo, useRef, useState } from "react";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Select } from "antd";
import ErrorBoundary from "components/error-boundary";
import InviteProjectUser from "admin-app/components/invite-project-user";
import { useHistory, useParams } from "react-router";
import { useQuerySubscription } from "hooks/common";
import { getGQL } from "utils/utils";
import CiqAgSearch from "components/ciq-ag-search";
import {
  dummyColumnDef,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import { DateUtils } from "utils/dateutils";
import CiqAgGridUtil from "admin-app/components/ag-grid-util";
import { PROJECT_USERS_BY_PROJECT } from "admin-app/services/ciq-queries";

type TProjectUser = {
  id: string;
  created_at: string;
  status?: {
    id: number;
    name: string;
  };
  user?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
  };
  project_role?: {
    id: number;
    name?: string;
  };
  subscription_vendor?: {
    id: string;
    name?: string;
  };
  created_by_user?: {
    email?: string;
  };
};

function ProjectUsers({
  subscriptionPK,
  subId
}: {
  subId: string;
  subscriptionPK: any;
}) {
  const gridRef = useRef<AgGridReact<TProjectUser>>(null);
  const history = useHistory();
  const { projectId, orgId } = useParams() as {
    projectId?: string;
    orgId: string;
  };
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);

  const defaultVendorId = subscriptionPK?.subscription_by_pk?.default_vendor_id;

  const { query: qQuery, sub: sQuery } = getGQL(PROJECT_USERS_BY_PROJECT);
  const { data: projectUsersData, loading } = useQuerySubscription<{
    project_users: Array<TProjectUser>;
  }>(qQuery, sQuery, {
    variables: {
      where: { project_id: { _eq: projectId } }
    },
    skip: !projectId
  });

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    if (!projectId) {
      api.setGridOption("loading", false);
      return;
    }

    api.setGridOption("loading", loading);
  }, [loading, projectId]);

  const projectOptions = useMemo(() => {
    const projects = subscriptionPK?.subscription_by_pk?.projects_owned ?? [];
    return projects.map((p: { id: string; name: string }) => ({
      label: p.name,
      value: p.id
    }));
  }, [subscriptionPK]);

  const columnDefs: ColDef<TProjectUser>[] = useMemo(
    () => [
      dummyColumnDef,
      {
        headerName: "Serial No.",
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        headerTooltip: "Serial No.",
        width: 100,
        sortable: false,
        editable: false,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "name",
        headerName: "Name",
        headerTooltip: "Name",
        valueGetter: ({ data }) => {
          const first = data?.user?.first_name ?? "";
          const last = data?.user?.last_name ?? "";
          return `${first} ${last}`.trim();
        }
      },
      {
        colId: "company",
        headerName: "Company Name",
        headerTooltip: "Company Name",
        valueGetter: ({ data }) => data?.subscription_vendor?.name ?? ""
      },
      { colId: "email", field: "user.email", headerName: "Email" },
      {
        colId: "phone",
        field: "user.phone",
        headerName: "Phone Number",
        headerTooltip: "Phone Number"
      },
      {
        colId: "project_role",
        headerName: "Project Role",
        headerTooltip: "Project Role",
        valueGetter: ({ data }) => data?.project_role?.name ?? ""
      },
      {
        colId: "status",
        headerName: "User Status",
        headerTooltip: "User Status",
        valueGetter: ({ data }) => data?.status?.name ?? ""
      },
      {
        colId: "created_by",
        field: "created_by_user.email",
        headerName: "Created By",
        headerTooltip: "Created By"
      },
      {
        colId: "created_at",
        field: "created_at",
        headerName: "Created At",
        headerTooltip: "Created At",
        initialWidth: 160,
        valueGetter: ({ data }) =>
          data?.created_at ? DateUtils.format(data.created_at) : ""
      }
    ],
    []
  );

  const defaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        comparator: comparatorWithEmptyAtBottom
      }) as ColDef<TProjectUser>,
    []
  );

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const rowData = useMemo(() => {
    if (!projectId) return [];
    return projectUsersData?.project_users ?? [];
  }, [projectId, projectUsersData?.project_users]);

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Project Users",
          totalCount: rowData?.length,
          filterStats: statsData
        },
        () => {
          gridRef.current?.api.resetColumnState();
        }
      ),
    [rowData?.length, statsData]
  );

  return (
    <div className="h-[calc(100vh-180px)]">
      <div className="flex w-full justify-between items-center px-6 py-2">
        <div className="flex space-x-3 items-center">
          <div className="flex space-x-1 items-center">
            <div>Project:</div>
            <Select
              showSearch
              placeholder="Select Project"
              className="w-60"
              value={projectId}
              onChange={(value) => {
                history.push(
                  `/organizations/${orgId}/subscriptions/${subId}/project/${value}`
                );
              }}
              data-testid="project-users-project-select"
              options={projectOptions}
              optionFilterProp="label"
            />
          </div>
          {statusBudge}
        </div>
        <div className="flex items-center gap-2">
          <CiqAgSearch
            placeholder="Search"
            gridRef={gridRef}
            data-testid="project-users-search"
          />
          <Button
            type="primary"
            disabled={!projectId}
            onClick={() => setInviteDrawerOpen(true)}
            data-testid="invite-project-user-button"
          >
            Create User
          </Button>
        </div>
      </div>
      <CiqAgGridUtil
        gridRef={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onFilterChanged={onFiltersApplied}
        overlayNoRowsTemplate={
          projectId ? "No Data Found" : "Select a project to view users"
        }
      />
      {inviteDrawerOpen && projectId && (
        <ErrorBoundary
          onError={() => {
            setInviteDrawerOpen(false);
          }}
        >
          <InviteProjectUser
            existingUsers={rowData}
            setDrawerOpen={setInviteDrawerOpen}
            showDrawerOpen={inviteDrawerOpen}
            modelTitle="Create User"
            projectId={projectId}
            subscriptionId={subId}
            defaultVendorId={defaultVendorId}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

export default ProjectUsers;
